import React, { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  useEntityList,
  createEntity,
  updateEntity,
  deleteEntity,
  roleSatisfies,
  useOrgInvites,
  createInvite,
  revokeInvite,
  friendlyError,
  ROLES,
  workspaceRole,
  useOrgJoinRequests,
  decideJoinRequest,
} from '../../data/entities';

// Access requests: employees whose verified company email is on a domain
// this organization has proven (Administration → Identity → Domains) asked
// to join. Approve with a role → the claims worker seats them within
// seconds and mails them; decline → they are told. Owners cannot be granted
// here (transfer ownership from Administration → Structure).
function AccessRequests({ requests }) {
  const [roles, setRoles] = useState({});
  const [err, setErr] = useState('');
  const open = (requests || []).filter((r) => r.status === 'pending');
  const recent = (requests || []).filter((r) => r.status !== 'pending').slice(0, 5);
  if (!requests || (!open.length && !recent.length)) return null;
  return (
    <>
      <h3 id="requests" style={{ marginTop: 22 }}>Access requests {open.length ? <span className="cs-help" style={{ fontSize: 12 }}>· {open.length} waiting</span> : null}</h3>
      {err && <p className="cs-help" style={{ color: 'var(--danger, #c33)' }}>{err}</p>}
      <div style={{ display: 'grid', gap: 8 }}>
        {open.map((r) => (
          <div key={r.id} className="cs-card" style={{ padding: 12, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ display: 'grid', gap: 2 }}>
              <span><strong style={{ color: 'var(--text)' }}>{r.displayName || r.email}</strong> <span className="cs-help" style={{ fontSize: 12 }}>{r.email}</span></span>
              <span className="cs-help" style={{ fontSize: 12 }}>
                verified <code>@{String(r.email).split('@')[1]}</code> · asked {r.createdAt ? new Date(r.createdAt).toLocaleString() : ''}{r.note ? ` · "${r.note}"` : ''}
              </span>
            </div>
            <span style={{ flex: 1 }} />
            <select className="cs-input" style={{ width: 120 }} value={roles[r.id] || 'member'} onChange={(e) => setRoles({ ...roles, [r.id]: e.target.value })} aria-label={`Role for ${r.email}`}>
              <option value="viewer">viewer</option>
              <option value="member">member</option>
              <option value="admin">admin</option>
            </select>
            <button className="cs-btn cs-btn-primary cs-btn-sm" onClick={() => decideJoinRequest(r.id, 'approve', { role: roles[r.id] || 'member' }).catch((e) => setErr(friendlyError(e)))}>Approve</button>
            <button className="cs-btn cs-btn-outline cs-btn-sm" data-agent-noclick onClick={() => decideJoinRequest(r.id, 'decline').catch((e) => setErr(friendlyError(e)))}>Decline</button>
          </div>
        ))}
        {recent.map((r) => (
          <div key={r.id} className="cs-card" style={{ padding: 10, display: 'flex', alignItems: 'center', gap: 10, opacity: 0.7 }}>
            <span>{r.email}</span>
            <span className="cs-help" style={{ fontSize: 12 }}>
              {r.status === 'done' ? `joined as ${r.role || 'member'}` : r.status === 'approved' ? 'approved — seating…' : r.status === 'declined' ? 'declined' : r.status === 'error' ? `failed: ${r.error || ''}` : r.status}
              {r.decidedAt ? ` · ${new Date(r.decidedAt).toLocaleDateString()}` : ''}
            </span>
          </div>
        ))}
      </div>
    </>
  );
}

// Per-workspace seats: orgs/{o}/workspaces/{w}/wsMembers/{uid}. Org
// owner/admin pass every workspace by rule and are shown as such; everyone
// else only sees inside workspaces they hold a seat in. A workspace admin
// may seat viewers/members/admins; only an org admin may seat an owner.
function WorkspaceSeats({ org, myRole, tenant, members }) {
  const workspaces = tenant?.workspaces || [];
  const [wsId, setWsId] = useState(workspaces[0]?.id || '');
  const ws = workspaces.find((w) => w.id === wsId) || workspaces[0] || null;
  const wid = ws?.id || null;
  const { docs: seats, error } = useEntityList('workspaceMember', { org: org.id, workspace: wid }, { orderBy: 'createdAt', dir: 'asc', enabled: !!wid });
  const myWsRole = workspaceRole(myRole, tenant?.wsRoles, wid);
  const canSeat = roleSatisfies(myWsRole, 'admin');
  const orgAdmin = roleSatisfies(myRole, 'admin');
  const [pick, setPick] = useState('');
  const [role, setRole] = useState('member');
  const [err, setErr] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestRole, setGuestRole] = useState('member');
  const [guestNote, setGuestNote] = useState('');
  const seated = new Set((seats || []).map((s) => s.id));
  const candidates = (members || []).filter((m) => !seated.has(m.id) && !roleSatisfies(m.role, 'admin'));
  const admins = (members || []).filter((m) => roleSatisfies(m.role, 'admin'));

  if (!workspaces.length) return <p className="cs-help" style={{ marginTop: 16 }}>No workspaces yet — create one from Home. Seats are granted per workspace.</p>;
  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <select className="cs-input" style={{ width: 240 }} value={wid || ''} onChange={(e) => setWsId(e.target.value)} aria-label="Workspace">
          {workspaces.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
        </select>
        <span className="cs-help" style={{ fontSize: 12 }}>your role here: {myWsRole || 'no seat'}</span>
      </div>
      {canSeat && (
        <form
          style={{ display: 'flex', gap: 8, maxWidth: 640, flexWrap: 'wrap', marginTop: 12 }}
          onSubmit={async (e) => {
            e.preventDefault();
            const m = candidates.find((x) => x.id === pick);
            if (!m || !wid) return;
            setErr('');
            try {
              await createEntity('workspaceMember', { org: org.id, workspace: wid }, { uid: m.uid, email: m.email, displayName: m.displayName || '', role }, { id: m.uid });
              setPick('');
            } catch (ex) { setErr(friendlyError(ex)); }
          }}
        >
          <select className="cs-input" style={{ flex: 1, minWidth: 220 }} value={pick} onChange={(e) => setPick(e.target.value)} aria-label="Member to seat">
            <option value="">Seat an org member…</option>
            {candidates.map((m) => <option key={m.id} value={m.id}>{m.displayName || m.email} · {m.email}</option>)}
          </select>
          <select className="cs-input" style={{ width: 120 }} value={role} onChange={(e) => setRole(e.target.value)} aria-label="Workspace role">
            {ROLES.filter((r) => r !== 'owner' || orgAdmin).map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <button className="cs-btn cs-btn-primary" disabled={!pick} type="submit">Seat</button>
        </form>
      )}
      {orgAdmin && (
        // People from outside this org (members of another org, or new to
        // nventr): an org invite that carries the seat. The claims worker
        // creates the viewer membership + the seat; someone who already has
        // an account lands here within a minute, nothing to click.
        <form
          style={{ display: 'flex', gap: 8, maxWidth: 640, flexWrap: 'wrap', marginTop: 10 }}
          onSubmit={async (e) => {
            e.preventDefault();
            const em = guestEmail.trim().toLowerCase();
            if (!em || !wid) return;
            setErr('');
            setGuestNote('');
            try {
              const existing = (members || []).find((m) => (m.email || '').toLowerCase() === em);
              if (existing) {
                if (roleSatisfies(existing.role, 'admin')) { setGuestNote(`${em} is an org ${existing.role} and already sees every workspace.`); return; }
                if (seated.has(existing.id)) { setGuestNote(`${em} is already seated here.`); return; }
                await createEntity('workspaceMember', { org: org.id, workspace: wid }, { uid: existing.uid, email: existing.email, displayName: existing.displayName || '', role: guestRole }, { id: existing.uid });
                setGuestNote(`Seated ${em} in ${ws.name} as ${guestRole}.`);
              } else {
                await createInvite(org, { email: em, role: 'viewer', workspace: ws, workspaceRole: guestRole });
                setGuestNote(`${em} will be seated in ${ws.name} as ${guestRole} — automatically within a minute if they already have an nventr account, otherwise as soon as they accept the emailed invite. Outside this workspace they are an org viewer.`);
              }
              setGuestEmail('');
            } catch (ex) { setErr(friendlyError(ex)); }
          }}
        >
          <input
            className="cs-input"
            style={{ flex: 1, minWidth: 220 }}
            type="email"
            placeholder="Add by email — anyone, including members of other organizations…"
            value={guestEmail}
            onChange={(e) => setGuestEmail(e.target.value)}
            aria-label="Email to seat"
          />
          <select className="cs-input" style={{ width: 120 }} value={guestRole} onChange={(e) => setGuestRole(e.target.value)} aria-label="Workspace role for the invitee">
            {ROLES.filter((r) => r !== 'owner').map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <button className="cs-btn cs-btn-outline" disabled={!guestEmail.trim()} type="submit">Add</button>
        </form>
      )}
      {guestNote && <p className="cs-help" style={{ marginTop: 8 }}>{guestNote}</p>}
      {(err || error) && <p className="cs-help" style={{ color: 'var(--danger, #c33)', marginTop: 8 }}>{err || error}</p>}
      <div style={{ display: 'grid', gap: 8, marginTop: 12 }}>
        {admins.map((m) => (
          <div key={`org-${m.id}`} className="cs-card" style={{ padding: 12, display: 'flex', alignItems: 'center', gap: 10, opacity: 0.8 }}>
            <strong style={{ color: 'var(--text)' }}>{m.displayName || m.email}</strong>
            <span className="cs-help" style={{ fontSize: 12 }}>{m.email}</span>
            <span style={{ flex: 1 }} />
            <span className="cs-help" title="Org owners and admins see every workspace">org {m.role} · all workspaces</span>
          </div>
        ))}
        {(seats || []).filter((s) => !admins.some((a) => a.id === s.id)).map((s) => (
          <div key={s.id} className="cs-card" style={{ padding: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
            <strong style={{ color: 'var(--text)' }}>{s.displayName || s.email}</strong>
            <span className="cs-help" style={{ fontSize: 12 }}>{s.email}</span>
            <span style={{ flex: 1 }} />
            {canSeat ? (
              <select
                className="cs-input"
                style={{ width: 120 }}
                value={s.role}
                onChange={(e) => updateEntity('workspaceMember', { org: org.id, workspace: wid }, s.id, { role: e.target.value }).catch((ex) => setErr(friendlyError(ex)))}
              >
                {ROLES.filter((r) => r !== 'owner' || orgAdmin || s.role === 'owner').map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            ) : <span className="cs-help">{s.role}</span>}
            {canSeat && (
              <button
                className="cs-btn cs-btn-outline cs-btn-sm"
                data-agent-noclick
                onClick={async () => {
                  // eslint-disable-next-line no-alert
                  if (window.confirm(`Remove ${s.email} from ${ws.name}?`)) {
                    await deleteEntity('workspaceMember', { org: org.id, workspace: wid }, s.id).catch((ex) => setErr(friendlyError(ex)));
                  }
                }}
              >
                Unseat
              </button>
            )}
          </div>
        ))}
        {seats && !seats.length && !admins.length && <p className="cs-help">Nobody is seated here yet.</p>}
      </div>
    </div>
  );
}

export default function MembersModule() {
  const { org, myRole, tenant } = useOutletContext();
  const { docs: members } = useEntityList('member', { org: org.id }, { orderBy: 'createdAt', dir: 'asc' });
  const invites = useOrgInvites(org.id);
  const isAdmin = roleSatisfies(myRole, 'admin');
  const requests = useOrgJoinRequests(isAdmin ? org.id : null);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('member');
  const [lastLink, setLastLink] = useState('');
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState('org');

  const pending = (invites || []).filter((i) => i.status === 'pending');

  return (
    <div data-nova-note={`Members (${tab} tab): ${(members || []).length} members, ${pending.length} pending invites. NoVA: invite_member (email + role, optional workspace seat), list_member; role changes, revoking and approving join requests are the person's clicks.`}>
      <h1 style={{ margin: 0 }}>Members</h1>
      <div className="cs-tabs" style={{ display: 'flex', gap: 6, marginTop: 12 }} role="tablist">
        <button type="button" role="tab" aria-selected={tab === 'org'} className={`cs-btn cs-btn-sm ${tab === 'org' ? 'cs-btn-primary' : 'cs-btn-outline'}`} onClick={() => setTab('org')}>Organization</button>
        <button type="button" role="tab" aria-selected={tab === 'ws'} className={`cs-btn cs-btn-sm ${tab === 'ws' ? 'cs-btn-primary' : 'cs-btn-outline'}`} onClick={() => setTab('ws')}>Workspaces</button>
      </div>
      <p className="cs-help" style={{ marginTop: 8, fontSize: 12 }}>
        {tab === 'org'
          ? 'Organization roles: viewer (read-only) · member · admin · owner. Owners and admins see every workspace. Admins invite people by email (up to admin); the owner seat is not invited — it is transferred from Administration → Structure. People who sign up with a verified email on a domain this organization has proven appear below as access requests.'
          : 'Workspace seats segregate everything inside a workspace — apps, agents, knowledge, projects. A member without a seat cannot see in. To authorize someone from another organization for this workspace\'s projects, add them by email: they join as an org viewer and get the seat.'}
      </p>
      {tab === 'ws' && <WorkspaceSeats org={org} myRole={myRole} tenant={tenant} members={members} />}
      {tab === 'org' && isAdmin && (
        <form
          style={{ display: 'flex', gap: 8, maxWidth: 640, flexWrap: 'wrap', marginTop: 16 }}
          onSubmit={async (e) => {
            e.preventDefault();
            if (!email.trim() || busy) return;
            setBusy(true);
            try {
              const token = await createInvite(org, { email: email.trim(), role });
              // The backend emails the invitee (claimsWorker → org-invite);
              // the link is shown too for a direct hand-over.
              setLastLink(`${window.location.origin}/orgs?invite=${token}`);
              setEmail('');
            } finally {
              setBusy(false);
            }
          }}
        >
          <input
            className="cs-input"
            style={{ flex: 1, minWidth: 220 }}
            placeholder="Invite by email…"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <select className="cs-input" style={{ width: 120 }} value={role} onChange={(e) => setRole(e.target.value)}>
            <option value="viewer">viewer</option>
            <option value="member">member</option>
            <option value="admin">admin</option>
          </select>
          <button className="cs-btn cs-btn-primary" disabled={!email.trim() || busy} type="submit">
            Invite
          </button>
        </form>
      )}
      {tab === 'org' && lastLink && (
        <p className="cs-help" style={{ marginTop: 8 }}>
          Invite created — an email with a join link is on its way; the invitee also sees it on their
          Organizations page after signing in with that email. Shareable link: <code>{lastLink}</code>
        </p>
      )}

      {tab === 'org' && <div style={{ display: 'grid', gap: 8, marginTop: 16 }}>
        {(members || []).map((m) => (
          <div key={m.id} className="cs-card" style={{ padding: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
            <strong style={{ color: 'var(--text)' }}>{m.displayName || m.email}</strong>
            <span className="cs-help" style={{ fontSize: 12 }}>{m.email}</span>
            <span style={{ flex: 1 }} />
            {isAdmin && m.uid !== org.ownerUid ? (
              <select
                className="cs-input"
                style={{ width: 120 }}
                value={m.role}
                onChange={(e) => updateEntity('member', { org: org.id }, m.id, { role: e.target.value })}
              >
                <option value="viewer">viewer</option>
                <option value="member">member</option>
                <option value="admin">admin</option>
              </select>
            ) : (
              <span className="cs-help">{m.role}</span>
            )}
            {isAdmin && m.uid !== org.ownerUid && (
              <button
                className="cs-btn cs-btn-outline cs-btn-sm"
                data-agent-noclick
                onClick={async () => {
                  // eslint-disable-next-line no-alert
                  if (window.confirm(`Remove ${m.email} from ${org.name}?`)) {
                    await deleteEntity('member', { org: org.id }, m.id);
                  }
                }}
              >
                Remove
              </button>
            )}
          </div>
        ))}
      </div>}

      {tab === 'org' && isAdmin && <AccessRequests requests={requests} />}

      {tab === 'org' && isAdmin && pending.length > 0 && (
        <>
          <h3 style={{ marginTop: 22 }}>Pending invites</h3>
          <div style={{ display: 'grid', gap: 8 }}>
            {pending.map((inv) => (
              <div key={inv.id} className="cs-card" style={{ padding: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
                <span>{inv.email}</span>
                <span className="cs-help" style={{ fontSize: 12 }}>as {inv.role}{inv.workspaceId ? ` · ${inv.workspaceName || inv.workspaceId} ${inv.workspaceRole || 'member'}` : ''}</span>
                <span style={{ flex: 1 }} />
                <button className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => revokeInvite(inv.id)} data-agent-noclick>
                  Revoke
                </button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
